<!-- DEV NOTE: Consider merging BaseShowElement into/with BaseFormElement -->
<template>
  <div class="show-element d-flex align-self-center">
    <div class="py-1" style="width:100%;">
      <slot :name="enumeratedShowElement.key" :value="value" :item="showData">
        <!-- Default content here -->
        <template v-if="(value == null || value == '') && enumeratedShowElement.transform !== 'boolean'">
          -
        </template>
        <BaseStatus
          v-else-if="enumeratedShowElement.transform === 'status'"
          :status="value"
          :mode="mode"
          full
        />
        <template v-else-if="enumeratedShowElement.transform === 'currentPhase'">
          <CurrentPhaseItem :object="value" show-users inline />
        </template>
        <!-- DEV NOTE: Refactor this functionality -->
        <span v-else-if="isDateTransform(enumeratedShowElement.transform)">
          <template v-if="mode != 'index'">
            {{  capitalizeIfNoCapitals(formattedDate(value)) }}
          </template>
          <template v-else>
             <template v-if="enumeratedShowElement.transform != 'date.index' ">
              {{ capitalizeIfNoCapitals(formattedDate(value)) }}
            </template>
            <template v-else>
              {{ capitalizeIfNoCapitals($d(safeParseISO(value), 'date.indexFirstRule')) }}
              <br />
              {{ capitalizeIfNoCapitals($d(safeParseISO(value), 'date.indexSecondRule')) }}
            </template>
          </template>
        </span>
        <span v-else-if="enumeratedShowElement.transform === 'boolean'">
          <BaseTrueOrFalse :bool="value" />
        </span>
        <span v-else-if="enumeratedShowElement.transform === 'currency'">
          {{ formatCurrency(value) }}
        </span>
        <!-- CHIP -->
        <span v-else-if="enumeratedShowElement.transform === 'chip'">
          <v-chip
            v-for="(chip, index) in toSingleElementArray(value)"
            class="mr-2"
            small
            :key="index"
          >
            {{ chip }}
          </v-chip>
        </span>
        <template v-else-if="enumeratedShowElement.chip">
          <span>
            <CustomChip
              :object="showData" 
              :avatar="enumeratedShowElement.chip.avatar"
              :object-text="enumeratedShowElement.chip.objectText"
              :avatar-text="enumeratedShowElement.chip.avatarText"
              :text-order="enumeratedShowElement.chip.textOrder || [enumeratedShowElement.key]"
              :goTo="enumeratedShowElement.chip.goTo"
            />
          </span>
        </template>
        <template v-else-if="enumeratedShowElement.transform === 'phoneNumber'">
          <span>
            <PhoneNumberShowElement :value="value" />
          </span>
        </template>
        <template v-else-if="enumeratedShowElement.transform === 'email'">
          <span>
            <EmailShowElement :value="value" />
          </span>
        </template>
        <span v-else-if="enumeratedShowElement.transform === 'html'">
          <v-card-text class="preview pa-0" v-html="value" />
        </span>
        <template v-else-if="isLocale(value)">
          <span>
            <LocaleItem :item="currentLocale" />
          </span>
        </template>
        <div v-else class="custom-text-truncate-2">
          {{ capitalizeIfNoCapitals(value) }}
        </div>
      </slot>
    </div>
  </div>
</template>

<script>
import { capitalize, includes, isString } from 'lodash';
import { parseISO, isDate, } from 'date-fns';
import { formatCurrency, } from '@/functions/utils';
import { safeParseISO } from '@/functions/DateHelpers';
import hasSlot from '@/mixins/hasSlot';
import availableLocales from '@/mixins/availableLocales';
import restructureShowElements from '@/mixins/restructureShowElements';

export default {
  name: 'BaseShowElement',
  mixins: [hasSlot, availableLocales, restructureShowElements],
  components: {
    CustomChip: () => import(/* webpackChunkName: "custom-chip" */'@/components/CustomChip.vue'),
    PhoneNumberShowElement: () => import(/* webpackChunkName: "phone-number-show-element" */'@/components/PhoneNumberShowElement.vue'),
    EmailShowElement: () => import(/* webpackChunkName: "email-show-element" */'@/components/EmailShowElement.vue'),
    LocaleItem: () => import(/* webpackChunkName: "locale-item" */'@/components/select/LocaleItem.vue'),
    CurrentPhaseItem: () => import(/* webpackChunkName: "current-phase-item" */'@/components/CurrentPhaseItem.vue'),
  },
  props: {
    mode: {
      type: String,
      default: undefined,
    },
    value: {
      type: null,
      required: true,
    },
    //only used for passing the whole formData object to slot
    showData: {
      type: Object,
      default: undefined,
    },
    showElement: {
      type: [String, Object],
      default: undefined,
      required: true,
    },
    translationParentKey: {
      type: String,
      default: undefined,
    },
  },
  data() {
    return {
    };
  },
  computed: {
    enumeratedShowElement() {
      let temp = this.showElement || {};
      temp['value'] = this.value;
      return this.restructureElement(temp);
    },
    show() {
      return this.enumeratedShowElement?.isElementRestructured;
    },
  },
  methods: {
    capitalize,
    includes,
    parseISO,
    isDate,
    formatCurrency,
    safeParseISO,
    toSingleElementArray(input) {
      if (input)
        return Array.isArray(input) ? [...input] : [input];
      else
        return [];
    },
    //DEV NOTE: eventually app-wide use this function
    capitalizeIfNoCapitals(str) {
      if (!isString(str) || str.length === 0) return str;
      if (str === str.toLowerCase()) {
        return capitalize(str);
      }
      return str;
    },
    formattedDate(value) {
      let date = safeParseISO(value);
      if (!date) return '-';

      // if transform is 'date.extended' use it directly, otherwise if is just 'datetime' then use 'datetime.default',
      // if transform is not given at all, but we already know that it is a date, then use 'date.default'
      return this.$d(date, includes(this.enumeratedShowElement.transform, '.') ? this.enumeratedShowElement.transform : `${this.enumeratedShowElement.transform || 'date'}.default`);
    },
  },
}
</script>
<style lang="scss" scoped>
.show-element {
  @include text-value;
  @include text-value-color;
  @include show-form-element-value-layout;
  @include show-form-element-value-border-color;
}
.preview::v-deep {
  min-height: 1em;
  
  p {
    margin-bottom: 0px !important;
  }
}
</style>